import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import { Button } from 'reactstrap';
import {
  clearBussesMaintenanceStay,
  getExtendedBusMaintenanceStays,
  clearExtendedBusMaintenanceStays,
} from '../../../../actions/mechanical-maintenance/BusMaintenanceStay';
import FormFooter from '../../../common/forms/FormFooter';
import Table from '../../../common/Table';
import DownloadButton from '../../../common/button/DownloadButton';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import Modal from '../../../common/modal/Modal';
import {
  COLOR,
  BUS_MAINTENANCE_STATUS_TYPE,
  DEFAULT_PAGE_SIZE,
} from '../../../../config/constants';
import { generateBusMaintenanceStayColumnsForBulk } from '../../../../config/columns';
import BusMaintenanceStayForm from './BusMaintenanceStayForm';
import ExtendedBusMaintenanceModal from './ExtendedBusMaintenanceModal';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_TIME_FORMAT } from '../../../../config/locale';

const getSelectValue = (value) =>
  value && typeof value === 'object' ? value.value : value;

const convertBusMaintenanceStayToCsv = (rows) =>
  rows.map((row) => {
    const maintenanceStatus =
      BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus];

    return {
      ID: row.id,
      'Número de Bus':
        (row.registeredBusIdSelect && row.registeredBusIdSelect.label) ||
        (row.registeredBus && row.registeredBus.companyBusId) ||
        row.registeredBusId ||
        '-',
      'Tipo de Servicio': (row.serviceType && row.serviceType.name) || '-',
      Origen:
        (row.originCityIdSelect && row.originCityIdSelect.label) ||
        (row.originCity && row.originCity.name) ||
        row.originCityId ||
        '-',
      'Fecha de Llegada': row.estimatedArrivalDate
        ? tzNormalizeDate({
            date: row.estimatedArrivalDate,
            format: INT_DATE_TIME_FORMAT,
          })
        : '-',
      'Fecha de Entrega': row.estimatedCompletionDate
        ? tzNormalizeDate({
            date: row.estimatedCompletionDate,
            format: INT_DATE_TIME_FORMAT,
          })
        : '-',
      Estado:
        (maintenanceStatus && maintenanceStatus.label) ||
        row.maintenanceStayStatus ||
        '-',
      Observación: row.observation || '-',
    };
  });

const generateStyle = (row) => {
  let background = '';

  if (row) {
    const {
      original: { noArrivalTime, modified, markedForDelete },
    } = row;

    if (markedForDelete) {
      background = COLOR.danger;
    } else if (modified) {
      background = COLOR.success;
    } else if (noArrivalTime) {
      background = COLOR.warning;
    }
  }

  return { background };
};

const BusMaintenanceStayTable = ({
  onHandleBulkSave,
  dispatchClearBussesMaintenanceStay,
  dispatchGetExtendedBusMaintenanceStays,
  dispatchClearExtendedBusMaintenanceStays,
  loading,
  extendedMaintenanceLoading,
  bussesMaintenanceStay,
  extendedMaintenance,
}) => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Editar Estancia');
  const [modalBody, setModalBody] = useState(null);

  const [showExtendedMaintenanceModal, setShowExtendedMaintenanceModal] =
    useState(false);

  const [modifiedData, setModifiedData] = useState([]);
  const [deletedData, setDeletedData] = useState([]);

  useEffect(() => {
    dispatchGetExtendedBusMaintenanceStays();

    return () => {
      dispatchClearBussesMaintenanceStay();
      dispatchClearExtendedBusMaintenanceStays();
    };
  }, []);

  useEffect(() => {
    const content = bussesMaintenanceStay.get('content') || [];

    const newContent = content.map((row) => ({
      ...row,
      modified: false,
      markedForDelete: false,

      registeredBusIdSelect: row.registeredBusId
        ? {
            value: row.registeredBusId,
            label:
              (row.registeredBus && row.registeredBus.companyBusId) ||
              row.registeredBusId,
          }
        : null,

      originCityIdSelect: row.originCityId
        ? {
            value: row.originCityId,
            label: (row.originCity && row.originCity.name) || row.originCityId,
          }
        : null,

      maintenanceStayStatusSelect: row.maintenanceStayStatus
        ? BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus]
        : null,
    }));

    setData(newContent);
    setOriginalData(newContent);
    setModifiedData([]);
    setDeletedData([]);
  }, [bussesMaintenanceStay]);

  const onCloseModal = () => {
    setShowModal(false);
    setModalBody(null);
  };

  const handleOpenExtendedMaintenance = () => {
    setShowExtendedMaintenanceModal(true);
  };

  const handleCloseExtendedMaintenance = () => {
    setShowExtendedMaintenanceModal(false);
  };

  const getDataIndex = ({ id }) => data.findIndex((row) => row.id === id);

  const onSubmitForm = (formValues) => {
    const dataIndex = getDataIndex({
      id: formValues.id,
    });

    if (dataIndex < 0) {
      return;
    }

    const newData = [...data];

    newData[dataIndex] = {
      ...newData[dataIndex],

      registeredBusId: getSelectValue(formValues.registeredBusId),

      registeredBusIdSelect: formValues.registeredBusId,

      originCityId: getSelectValue(formValues.originCityId),

      originCityIdSelect: formValues.originCityId,

      maintenanceStayStatus: getSelectValue(formValues.maintenanceStayStatus),

      maintenanceStayStatusSelect: formValues.maintenanceStayStatus,

      observation: formValues.observation,

      estimatedArrivalDate: formValues.estimatedArrivalDate,

      estimatedCompletionDate: formValues.estimatedCompletionDate,

      noArrivalTime: formValues.noArrivalTime,

      modified: true,
      markedForDelete: false,
    };

    const existingIndex = modifiedData.findIndex(
      (item) => item.id === formValues.id,
    );

    if (existingIndex >= 0) {
      const newModifiedData = [...modifiedData];

      newModifiedData[existingIndex] = newData[dataIndex];

      setModifiedData(newModifiedData);
    } else {
      setModifiedData([...modifiedData, newData[dataIndex]]);
    }

    setDeletedData(deletedData.filter((item) => item.id !== formValues.id));

    setData(newData);
    onCloseModal();
  };

  const generateInitialValues = (row) => ({
    id: row.id,

    registeredBusId:
      row.registeredBusIdSelect ||
      (row.registeredBusId && {
        value: row.registeredBusId,
        label:
          (row.registeredBus && row.registeredBus.companyBusId) ||
          row.registeredBusId,
      }),

    originCityId:
      row.originCityIdSelect ||
      (row.originCityId && {
        value: row.originCityId,
        label: (row.originCity && row.originCity.name) || row.originCityId,
      }),

    maintenanceStayStatus:
      row.maintenanceStayStatusSelect ||
      (row.maintenanceStayStatus &&
        BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus]),

    observation: row.observation,

    estimatedArrivalDate:
      row.estimatedArrivalDate != null
        ? tzNormalizeDate({
            date: row.estimatedArrivalDate,
            format: INT_DATE_TIME_FORMAT,
          })
        : null,

    estimatedCompletionDate:
      row.estimatedCompletionDate != null
        ? tzNormalizeDate({
            date: row.estimatedCompletionDate,
            format: INT_DATE_TIME_FORMAT,
          })
        : null,

    noArrivalTime: row.noArrivalTime || false,
  });

  const handleEdit = (row) => {
    const body = (
      <BusMaintenanceStayForm
        edit
        initialValues={generateInitialValues(row)}
        onCancel={onCloseModal}
        onSubmit={onSubmitForm}
      />
    );

    const busNumber =
      (row.registeredBus && row.registeredBus.companyBusId) || row.id;

    setModalTitle(`Editar Estancia - Bus ${busNumber}`);

    setModalBody(body);
    setShowModal(true);
  };

  const handleMarkForDelete = (row) => {
    const index = getDataIndex(row);

    if (index < 0) {
      return;
    }

    const newData = [...data];

    if (newData[index].markedForDelete) {
      newData[index] = {
        ...originalData[index],
        modified: false,
        markedForDelete: false,
      };

      setDeletedData(deletedData.filter((item) => item.id !== row.id));

      setModifiedData(modifiedData.filter((item) => item.id !== row.id));
    } else {
      newData[index] = {
        ...newData[index],
        markedForDelete: true,
        modified: false,
      };

      setDeletedData([...deletedData, newData[index]]);

      setModifiedData(modifiedData.filter((item) => item.id !== row.id));
    }

    setData(newData);
  };

  const handleClearRow = (row) => {
    const index = getDataIndex(row);

    if (index < 0) {
      return;
    }

    const newData = [...data];

    newData[index] = {
      ...originalData[index],
      modified: false,
      markedForDelete: false,
    };

    setModifiedData(modifiedData.filter((item) => item.id !== row.id));

    setDeletedData(deletedData.filter((item) => item.id !== row.id));

    setData(newData);
  };

  const handleSaveAll = () => {
    onHandleBulkSave(modifiedData, deletedData);
  };

  const handleSaveExtendedBus = async (modifiedRows) => {
    if (!modifiedRows.length) {
      return;
    }

    await onHandleBulkSave(modifiedRows, []);

    await dispatchGetExtendedBusMaintenanceStays();
  };

  const editModal = (
    <Modal
      show={showModal}
      title={modalTitle}
      onClickClose={onCloseModal}
      body={modalBody}
      size="lg"
    />
  );

  const saveButtonDisabled =
    loading || (modifiedData.length === 0 && deletedData.length === 0);

  const pendingChangesCount = modifiedData.length + deletedData.length;

  return (
    <div className="mt-4">
      <div className="mb-2 d-flex justify-content-end align-items-center">
        {extendedMaintenance.size > 0 && (
          <Button
            type="button"
            color="warning"
            className="mr-2"
            onClick={handleOpenExtendedMaintenance}
          >
            Ver mantenimientos extendidos
          </Button>
        )}

        {data.length > 0 && (
          <DownloadButton
            data={convertBusMaintenanceStayToCsv(data)}
            csvName="Estancias de Mantenimiento de Buses.csv"
          />
        )}
      </div>

      <Table
        manual={false}
        columns={generateBusMaintenanceStayColumnsForBulk({
          handleEdit,
          handleMarkForDelete,
          handleClearRow,
        })}
        data={data}
        filterable
        defaultFilterMethod={filterMethodCaseInsensitive}
        loading={loading}
        generateStyle={generateStyle}
        defaultPageSize={DEFAULT_PAGE_SIZE}
      />

      <div className="mt-3">
        <FormFooter
          saveButtonAction={handleSaveAll}
          saveButtonDisabled={saveButtonDisabled}
          saveButtonText={
            pendingChangesCount > 0
              ? `Guardar Cambios (${pendingChangesCount})`
              : 'Guardar Cambios'
          }
          saveButtonType="button"
        />
      </div>

      {editModal}

      <ExtendedBusMaintenanceModal
        show={showExtendedMaintenanceModal}
        onClose={handleCloseExtendedMaintenance}
        extendedMaintenance={extendedMaintenance}
        onSaveBus={handleSaveExtendedBus}
        loading={extendedMaintenanceLoading}
      />
    </div>
  );
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => ({
  loading: MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'all',
    'loading',
  ]),

  bussesMaintenanceStay: MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'all',
    'content',
  ]),

  extendedMaintenance: MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'extendedMaintenance',
    'content',
  ]),

  extendedMaintenanceLoading:
    MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
      'extendedMaintenance',
      'loading',
    ]),
});

const mapDispatchToProps = {
  dispatchClearBussesMaintenanceStay: clearBussesMaintenanceStay,

  dispatchGetExtendedBusMaintenanceStays: getExtendedBusMaintenanceStays,

  dispatchClearExtendedBusMaintenanceStays: clearExtendedBusMaintenanceStays,
};

BusMaintenanceStayTable.propTypes = {
  loading: PropTypes.bool,

  extendedMaintenanceLoading: PropTypes.bool,

  dispatchClearBussesMaintenanceStay: PropTypes.func.isRequired,

  dispatchGetExtendedBusMaintenanceStays: PropTypes.func.isRequired,

  dispatchClearExtendedBusMaintenanceStays: PropTypes.func.isRequired,

  onHandleBulkSave: PropTypes.func.isRequired,

  bussesMaintenanceStay: PropTypes.instanceOf(Immutable.Map).isRequired,

  extendedMaintenance: PropTypes.instanceOf(Immutable.List).isRequired,
};

BusMaintenanceStayTable.defaultProps = {
  loading: false,
  extendedMaintenanceLoading: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BusMaintenanceStayTable);
